import { FileExcelOutlined } from '@ant-design/icons';
import { Button, Input, Space, Switch, Table } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { apiClient, apiErrorMessage, getApiBaseUrl } from '../../../utils';

export function DataPreviewPage(props: { integrationId?: string; filterName?: string }) {
  const [loading, setLoading] = useState(false);
  const [tableColumns, setTableColumns] = useState<any[]>();
  const [records, setRecords] = useState<any[]>();

  const [tableCurrent, setTableCurrent] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [tableTotal, setTableTotal] = useState(0);

  const [search, setSearch] = useState<string>();
  const [showChanged, setShowChanged] = useState(true);

  const loadRecords = useCallback(() => {
    setLoading(true);
    let offset = (tableCurrent - 1) * tablePageSize;
    const queryParams = [`limit=${tablePageSize}`, `offset=${offset}`, `sendtype=${showChanged ? 'changed' : 'all'}`];
    if (search) {
      queryParams.push(`search=${search}`);
    }
    if (props.filterName) {
      queryParams.push(`filtername=${props.filterName}`);
    }
    const queryParamsStr = queryParams.join('&');
    apiClient
      .get(`integrations/${props.integrationId}/preview?${queryParamsStr}`)
      .then(response => {
        const responseData = response.data.rows as any[];

        if (!responseData || !responseData.length) {
          setRecords(undefined);
          setTableTotal(0);
          setTableColumns(undefined);
          return;
        }

        setRecords(responseData);
        setTableTotal(response.data.count);

        const columns = Object.keys(responseData[0]).map(k => {
          return {
            title: k,
            dataIndex: k,
            key: k,
            render: (value: any) => {
              if (value === null || value === undefined) {
                return '';
              }
              if (Array.isArray(value)) {
                // Check if it's an array of objects
                if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
                  const headers = Object.keys(value[0]);
                  return (
                    <table style={{
                      fontSize: '11px',
                      border: '1px solid #d9d9d9',
                      borderCollapse: 'collapse',
                      maxWidth: '500px'
                    }}>
                      <thead>
                        <tr>
                          {headers.map((header, idx) => (
                            <th key={idx} style={{
                              border: '1px solid #d9d9d9',
                              padding: '4px 8px',
                              backgroundColor: '#fafafa',
                              fontWeight: 600
                            }}>
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {value.map((row, rowIdx) => (
                          <tr key={rowIdx}>
                            {headers.map((header, colIdx) => (
                              <td key={colIdx} style={{
                                border: '1px solid #d9d9d9',
                                padding: '4px 8px'
                              }}>
                                {row[header]?.toString() || ''}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                }
                // Array of primitives
                return JSON.stringify(value);
              }
              return value.toString();
            },
          };
        });
        setTableColumns(columns);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [tableCurrent, tablePageSize, search, props.filterName, props.integrationId, showChanged]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const onTableChange = (pagination: any) => {
    setTableCurrent(pagination.current);
    setTablePageSize(pagination.pageSize);
  };

  const onSearch = (input: string) => {
    input = input.trim();
    setSearch(input === '' ? undefined : input);
  };

  const onShowChangedChange = (value: boolean) => {
    setTableCurrent(1);
    setShowChanged(value);
  };

  const onExcelExportClick = () => {
    const queryParams = [`sendtype=${showChanged ? 'changed' : 'all'}`];
    if (props.filterName) {
      queryParams.push(`filtername=${props.filterName}`);
    }
    const queryParamsStr = queryParams.join('&');

    const baseApiUrl = getApiBaseUrl() + '/api/';
    const url = `${baseApiUrl}integrations/${props.integrationId}/preview/export?${queryParamsStr}`;
    window.open(url, '_blank');
  };

  return (
    <>
      <div style={{ textAlign: 'right', marginBottom: '1rem' }}>
        <Space>
          <Input.Search placeholder="Arama" allowClear={true} onSearch={onSearch} style={{ width: 200 }} />{' '}
          <Space>
            <Switch checked={showChanged} onChange={onShowChangedChange} />
            <span>Değişen kayıtlar</span>
          </Space>
          <Button type="primary" icon={<FileExcelOutlined />} onClick={onExcelExportClick}>
            Excel'e aktar
          </Button>
        </Space>
      </div>
      <Table
        columns={tableColumns}
        dataSource={records}
        loading={loading}
        scroll={{ x: '100%' }}
        pagination={
          search
            ? false
            : {
                current: tableCurrent,
                total: tableTotal,
                pageSize: tablePageSize,
                pageSizeOptions: ['10', '25', '50', '100'],
                position: ['bottomCenter'],
                showTotal: (total, range) => `Toplam ${total} kayıt. ${range[0]}-${range[1]} arası gösteriliyor.`,
                showSizeChanger: true,
              }
        }
        onChange={onTableChange}
      />
    </>
  );
}
